import { AppUserModel } from "@/core/models/app-user.model";
import { AppRoleModel } from "@/core/models/app-role.model";
import { AppRoleRepo } from "@/core/repos/tables/app-role.repo";
import { AppUserRepo } from "@/core/repos/tables/app-user.repo";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import container from "@/lib/di/container";
import { Modal, Button, ConfigProvider, Checkbox, message, Tree, TreeDataNode, Input } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { Dispatch, SetStateAction, useState, useEffect, useCallback, Key } from "react";
import { IPermissionGroup, IPermission } from "@/core/enums/permissions.enum";
import { normalizeVietnameseString } from "@/core/pipes/norm-vn.pipe";
import { GlobalUtils } from "@/app/home/utils/global.utils";

const appUserRepo = container.get(AppUserRepo);
const appRoleRepo = container.get(AppRoleRepo);

const COLUMNS_COUNT = 3;

interface AppRoleModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    data: AppUserModel
}
export const AppRoleModal: React.FC<AppRoleModalProps> = ({isModalOpen, setIsModalOpen, data}) => {

    const [roles, setRoles] = useState<AppRoleModel[]>([]);
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [viewPermissionModalOpen, setViewPermissionModalOpen] = useState<boolean>(false);
    const [selectedRoleForView, setSelectedRoleForView] = useState<AppRoleModel | null>(null);
    const [rolePermissions, setRolePermissions] = useState<string[]>([]);
    const [columnData, setColumnData] = useState<{column: IPermissionGroup[], nodes: TreeDataNode[]}[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [permissionGroups, setPermissionGroups] = useState<IPermissionGroup[]>([]);
    const [allowedPermissions, setAllowedPermissions] = useState<Key[]>([]);

    const init = useCallback(async () => {
        setSelectedRoles([]);
        const _selectedRoles = (await appUserRepo.getRoles(data.id)).map(role => role.name);
        setSelectedRoles(_selectedRoles);
        const _roles = await appRoleRepo.getAll();
        setRoles(_roles);
    }, [data]);

    useEffect(()=>{init()}, [init]);

    const transformClaimTemplatesToPermissionGroups = (templates: Array<{id: string, group: string, checked: boolean, name: string}>): IPermissionGroup[] => {
        const groupMap = new Map<string, IPermission[]>();
        
        templates.forEach(template => {
            if (!groupMap.has(template.group)) {
                groupMap.set(template.group, []);
            }
            groupMap.get(template.group)!.push({
                key: template.id,
                name: template.name
            });
        });

        return Array.from(groupMap.entries()).map(([groupName, permissions]) => ({
            name: groupName,
            permissions: permissions
        }));
    };

    const initViewPermissions = useCallback(async (role: AppRoleModel) => {
        try {
            const templates = await appRoleRepo.getClaimTemplates();
            const groups = transformClaimTemplatesToPermissionGroups(templates);
            setPermissionGroups(groups);
            
            // Calculate allowed permissions (all ids except "Admin")
            const allowed = templates
                .filter(t => t.id !== "Admin")
                .map(t => t.id);
            setAllowedPermissions(allowed);

            const claims = await appRoleRepo.getAllClaims(role.name);
            const permissionKeys = claims.map(claim => claim.id);
            setRolePermissions(permissionKeys);
        } catch (error) {
            console.error("Failed to fetch role permissions:", error);
            message.error("Không thể tải danh sách quyền");
        }
    }, []);

    const handleViewPermissions = async (role: AppRoleModel) => {
        setSelectedRoleForView(role);
        setViewPermissionModalOpen(true);
        setSearchQuery("");
        await initViewPermissions(role);
    };

    const generatePermissionTreeColumns = useCallback(() => {
        if (permissionGroups.length === 0) return;
        
        const groupMatch = (group: IPermissionGroup) => {
            const name = normalizeVietnameseString(group.name).toLowerCase();
            const query = normalizeVietnameseString(searchQuery).toLowerCase();
            if (name.includes(query)) return true;
            return false;
        };

        const permissionMatch = (group: IPermissionGroup, permission: IPermission) => {
            const isGroupMatched = groupMatch(group);
            if (isGroupMatched) return true;
            const query = normalizeVietnameseString(searchQuery).toLowerCase();
            const name = normalizeVietnameseString(permission.name).toLowerCase();
            const key = normalizeVietnameseString(permission.key).toLowerCase();
            if (key.includes(query)) return true;
            if (name.includes(query)) return true;
            return false;
        };

        const filteredGroups = permissionGroups
            .map((group): IPermissionGroup => ({
                name: group.name,
                permissions: group.permissions.filter((permission) => permissionMatch(group, permission))
            }))
            .filter(g => g.permissions.length > 0);

        const columns = GlobalUtils.divideArray(filteredGroups, COLUMNS_COUNT);
        
        const data = columns.map((column) => {
            return {
                column: column,
                nodes: column.map((group, index): TreeDataNode => ({
                    title: group.name,
                    key: index,
                    children: group.permissions.map((permission): TreeDataNode => ({
                        title: permission.name,
                        key: permission.key
                    }))
                }))
            }
        });
        setColumnData(data);
    }, [searchQuery, permissionGroups]);

    useEffect(() => {
        if (viewPermissionModalOpen && permissionGroups.length > 0) {
            generatePermissionTreeColumns();
        }
    }, [searchQuery, permissionGroups, viewPermissionModalOpen, generatePermissionTreeColumns]);

    const getCheckedPermission = (key: Key[], column: IPermissionGroup[]) => {
        const columnKeys = GlobalUtils.flattenArray(column.map((g: IPermissionGroup) => g.permissions.map(p => p.key)));
        const result = key.filter(key => columnKeys.includes(key));
        return result;
    };

    const isAllSelected = (key: Key[]) => {
        return (GlobalUtils.arrayContains(key, allowedPermissions));
    };

    const handleCloseViewPermissionModal = () => {
        setViewPermissionModalOpen(false);
        setSelectedRoleForView(null);
        setRolePermissions([]);
        setColumnData([]);
        setPermissionGroups([]);
        setAllowedPermissions([]);
        setSearchQuery("");
    };

    const handleCancel = () => {
        setIsModalOpen(false);
    };
    const handleSave = async () => {
        setLoading(true);
        const _roles = roles.filter(role => selectedRoles.includes(role.name));
        const result = await appUserRepo.changeRoles(data.id, _roles);
        setIsModalOpen(false);
        if (result) {
            message.success({
                content: "Đã cập nhật"
            })
        } else {
            message.error({
                content: "Cập nhật thất bại"
            })
        }
        setLoading(false);
    };
    return <>
    <Modal open={isModalOpen} onCancel={handleCancel} width={450} centered
        footer={[
            <div key={1} className="w-full flex flex-row justify-end px-1.5">
                <Button key="submit" type="primary" loading={loading}
                    style={{ 
                        background: "var(--primary-color)", 
                        paddingRight: 48, 
                        paddingLeft: 48, 
                        fontSize: 18,
                        fontWeight: 600,
                        paddingTop: 18, 
                        paddingBottom: 18,
                        }}
                    onClick={handleSave}
                >
                    LƯU
                </Button>
            </div>,
        ]}
        styles={{
            mask: {
                backdropFilter: 'blur(2px)',
                backgroundColor: 'var(--primary-mask)'
            }
        }}
    >
        <div className="flex flex-col justify-center items-start">
            <div className="flex flex-row w-full items-start justify-start select-none">
                <p className="ml-3 text-2xl font-bold text-primary">PHÂN QUYỀN</p>
            </div>
            <div className="mt-4 mb-3 px-3">
                <ConfigProvider theme={MODAL_THEME}>
                    <Checkbox.Group value={selectedRoles} onChange={(value) => setSelectedRoles(value)}>
                        <div className="flex flex-col">
                            {roles.map(role => (
                                <div className="my-1 flex flex-row items-center justify-between" key={role.name}>
                                    <Checkbox value={role.name}><p className="text-lg pl-2">{role.description}</p></Checkbox>
                                    <Button 
                                        type="text" 
                                        icon={<EyeOutlined />} 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewPermissions(role);
                                        }}
                                        className="ml-2"
                                    />
                                </div>
                            ))}
                        </div>
                    </Checkbox.Group>
                </ConfigProvider>
            </div>
        </div>
        <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
    </Modal>
    <Modal 
        open={viewPermissionModalOpen} 
        onCancel={handleCloseViewPermissionModal} 
        width={1000} 
        style={{top: 50}}
        footer={[
            <Button key="close" onClick={handleCloseViewPermissionModal}>
                ĐÓNG
            </Button>
        ]}
        styles={{
            mask: {
                backdropFilter: 'blur(2px)',
                backgroundColor: 'var(--primary-mask)'
            }
        }}
    >
        <div className="flex flex-col justify-start items-start select-none">
            <div className="flex flex-row w-full items-start justify-start select-none">
                <p className="ml-3 text-2xl font-bold text-primary">
                    QUYỀN CỦA ROLE: {selectedRoleForView?.description || ""}
                </p>
            </div>
            <div className="mt-4 mb-3 px-3 w-full">
                <ConfigProvider theme={MODAL_THEME}>
                    <div className="flex flex-col w-full items-start justify-start my-2">
                        <Input 
                            className="mx-2 mb-4" 
                            value={searchQuery} 
                            onChange={(e) => setSearchQuery(e.target.value)} 
                            placeholder="Tìm kiếm Quyền/Nhóm quyền" 
                            style={{width: "100%"}} 
                        />
                        <Checkbox 
                            className="px-2 pb-2" 
                            disabled={true} 
                            checked={isAllSelected(rolePermissions)}
                        >
                            Tất cả
                        </Checkbox>
                        <div className="grid grid-cols-3">
                            {columnData.map((col, index) => {
                                return <Tree
                                    key={index}
                                    className="mx-2"
                                    disabled={true}
                                    checkable
                                    checkedKeys={getCheckedPermission(rolePermissions, col.column)}
                                    treeData={col.nodes}
                                />
                            })}
                        </div>
                    </div>
                </ConfigProvider>
            </div>
        </div>
        <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
    </Modal>
    </>
} 